import { computed, ComputedRef, getCurrentInstance, onMounted, Ref, ref } from 'vue';
import { SearchGroupResult } from '../../../shared/models/responses/search/findGeneric.response';
import { groupAccessTypes } from '../../../shared/enums/groupAccessTypes';
import { groupMembershipRoles } from '../../../shared/enums/groupMembershipRoles';
import { groupTypes } from '../../../shared/enums/groupTypes';
import { types } from '../../store/types/types';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import { groupMembershipActions } from '../../../shared/enums/groupMembershipActions';
import { groupService } from '../../services/api/group.service';
import i18n from '../../../shared/locales/i18n';
import { useSearchResultGroupMembership } from './useSearchResultGroupMembership';
import { useUserProfileStore } from '../../../shared/stores';
import { useModal } from '../../../shared/composables/useModal';

export const useSearchResultGroup = (options: {
  group: ComputedRef<SearchGroupResult>;
  searchString: ComputedRef<string>;
}) => {
  const { proxy } = getCurrentInstance();
  const { profile } = useUserProfileStore();
  const { updateGroupMembershipRole } = useSearchResultGroupMembership();
  const { group } = options;
  const institutions = computed(() => proxy.$store.getters[types.GET_INSTITUTIONS]);
  const userGroups = computed(() => proxy.$store.getters[types.GET_USER_GROUPS]);
  const isLoading = ref(false);
  const selectedInstitutionProfileId: Ref<number | null> = ref(null);
  const { isVisible: isVisibleInstitutionSelection, show: showInstitutionSelection } = useModal();

  const institutionSelectModalTitle = computed(() => {
    if (group.value.access === groupAccessTypes.APPLICATION) {
      return i18n.t('user.apply_group');
    }
    return i18n.t('user.join_group');
  });

  const institutionSelectModalDescription = computed(() => {
    if (group.value.access === groupAccessTypes.APPLICATION) {
      return i18n.t('user.select_institution_group_apply');
    }
    return i18n.t('user.select_institution_group_join');
  });

  const institutionSelectModalButtonLabel = computed(() => {
    if (group.value.access === groupAccessTypes.APPLICATION) {
      return i18n.t('button.apply');
    }
    return i18n.t('button.register');
  });

  const action = computed(() => {
    const { access, membershipRole, type, admins } = group.value;
    const isMember =
      membershipRole === groupMembershipRoles.MEMBER || membershipRole === groupMembershipRoles.INDIRECT;
    const isApplied = membershipRole === groupMembershipRoles.APPLIED;
    const canJoin = !membershipRole || membershipRole === groupMembershipRoles.INACTIVE;
    const canApply = canJoin || membershipRole === groupMembershipRoles.APPLICATIONREMOVED;
    const isNotAdmin =
      admins == null ||
      admins.filter(adm => institutions.value.some(i => i.institutionProfileId === adm.institutionProfileId)).length ===
        0;

    if (
      (access === groupAccessTypes.OPEN || access === groupAccessTypes.APPLICATION) &&
      isMember &&
      type !== groupTypes.UNI &&
      isNotAdmin
    ) {
      return {
        labelKey: 'user.leave_group',
        iconName: iconClassEnum.USER_LEAVE,
        role: groupMembershipRoles.INACTIVE,
        buttonVariant: 'link',
      };
    }
    if (access === groupAccessTypes.APPLICATION && isApplied) {
      return {
        labelKey: 'user.cancel_application_group',
        iconName: iconClassEnum.REVERT,
        role: groupMembershipRoles.APPLICATIONREMOVED,
        buttonVariant: 'link',
      };
    }
    if (access === groupAccessTypes.OPEN && canJoin) {
      return {
        labelKey: 'user.join_group',
        iconName: iconClassEnum.USER_ADD,
        role: groupMembershipRoles.MEMBER,
        buttonVariant: 'primary',
      };
    }
    if (access === groupAccessTypes.APPLICATION && canApply) {
      return {
        labelKey: 'user.apply_group',
        iconName: iconClassEnum.LOCK_OUTLINE,
        role: groupMembershipRoles.APPLIED,
        buttonVariant: 'primary',
      };
    }
    return null;
  });

  const applicableInstitutions = computed(() => {
    if (group.value.type === groupTypes.MUNICIPAL) {
      return institutions.value.filter(inst => group.value.municipalityCode === inst.municipalityCode);
    }
    if (group.value.type === groupTypes.CROSS_INSTITUTIONAL) {
      return institutions.value.filter(inst =>
        inst.administrativeAuthority?.institutionCodes.includes(group.value.institutionCode)
      );
    }
    return institutions.value.filter(inst => group.value.institutionCode === inst.institutionCode);
  });

  const canViewGroupMembership = computed(() => {
    const { allowMembersToBeShown } = group.value;
    const isMember = userGroups.value.some(userGroup => userGroup.id === parseInt(group.value.id));

    return allowMembersToBeShown && isMember;
  });

  const getInstitutionProfileIds = (action: groupMembershipActions) => {
    if (action === groupMembershipActions.LEAVE) {
      return applicableInstitutions.value.map(institution => institution.institutionProfileId);
    } else {
      return [selectedInstitutionProfileId.value];
    }
  };

  const joinOrLeaveGroup = async (groupRole: groupMembershipRoles) => {
    const action =
      groupRole == groupMembershipRoles.INACTIVE || groupRole == groupMembershipRoles.APPLICATIONREMOVED
        ? groupMembershipActions.LEAVE
        : groupMembershipActions.JOIN;
    const institutionProfileIds = getInstitutionProfileIds(action);
    isLoading.value = true;
    await groupService.joinOrLeaveGroup({
      groupId: parseInt(group.value.id),
      groupAccessType: action,
      institutionProfileIds: institutionProfileIds,
    });
    isLoading.value = false;
    proxy.$showToast(getToastMessage(groupRole, group.value.title));
    updateGroupMembershipRole(group.value.id, groupRole);
    await updateUserGroups(groupRole);
  };

  const getToastMessage = (groupRole: groupMembershipRoles, groupName: string) => {
    switch (groupRole) {
      case groupMembershipRoles.MEMBER:
        return i18n.t('user.group_joined', { group: groupName }).toString();
      case groupMembershipRoles.APPLIED:
        return i18n.t('user.group_applied', { group: groupName }).toString();
      default:
        return i18n.t('user.group_left', { group: groupName }).toString();
    }
  };

  const openGroup = async (groupId: string) => {
    if (!group.value.currentUserCanAccessGroupDashboard) {
      return;
    }
    await proxy.$store.dispatch(types.SET_ACTIVE_GROUP, {
      groupId: groupId,
      institutionCode: profile.institutionProfile.institution.institutionCode,
      isOpennedGroupFromGlobalSearch: true,
    });
    await proxy.$router.push({
      name: 'group-dashboard',
      params: { groupId },
    });
  };

  const addGroup = () => {
    proxy.$store.commit(types.MUTATE_ADD_GROUP, group.value);
  };

  const removeGroup = () => {
    proxy.$store.dispatch(types.REMOVE_GROUP_FROM_USER_GROUPS, group.value.id);
  };

  const updateUserGroups = async (groupRole: groupMembershipRoles) => {
    if (groupRole === groupMembershipRoles.MEMBER) {
      addGroup();
    } else if (groupRole === groupMembershipRoles.INACTIVE) {
      removeGroup();
    }
    await getProfileContext();
  };

  const getProfileContext = () => proxy.$store.dispatch(types.ACTION_GET_PROFILE_CONTEXT);

  const handleGroupAction = async (groupRole: groupMembershipRoles) => {
    const shouldSelectInstitution =
      [groupMembershipRoles.MEMBER, groupMembershipRoles.APPLIED].includes(groupRole) &&
      applicableInstitutions.value.length > 1;
    if (shouldSelectInstitution) {
      isVisibleInstitutionSelection.value = true;
      return;
    }
    await joinOrLeaveGroup(groupRole);
  };

  onMounted(() => {
    selectedInstitutionProfileId.value = applicableInstitutions.value[0].institutionProfileId;
  });

  return {
    action,
    isVisibleInstitutionSelection,
    institutionSelectModalTitle,
    institutionSelectModalDescription,
    institutionSelectModalButtonLabel,
    selectedInstitutionProfileId,
    applicableInstitutions,
    isLoading,
    canViewGroupMembership,
    showInstitutionSelection,
    openGroup,
    joinOrLeaveGroup,
    handleGroupAction,
  };
};
